import { Navigate } from 'react-router-dom';
import { useMemo } from 'react';
import { hasPageAccess, isSystemAdmin } from '../utils/permissions.js';
import { ROUTES, STORAGE_KEYS } from '../utils/constants.js';
import { useSystemAdminRegisterAvailable } from '../hooks/queries/authQueries.js';
import { useAuthContext } from '../contexts/AuthContext.jsx';

/**
 * 토큰이 유효한지 확인하는 함수
 */
const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    // 토큰 만료 시간 확인
    return payload.exp > currentTime;
  } catch (error) {
    console.error('토큰 파싱 오류:', error);
    return false;
  }
};

/**
 * 보호된 라우트 컴포넌트
 * 사용자의 인증 상태와 권한에 따라 페이지 접근을 제어합니다.
 * 권한은 로컬 스토리지에 저장된 피처 권한을 기반으로 확인합니다.
 */
const ProtectedRoute = ({ children, path }) => {
  const { user } = useAuthContext();

  // 로컬 스토리지에서 직접 토큰 확인 (새로고침 시 AuthContext 동기화 전에도 동작)
  const sessionStatus = useMemo(() => {
    // 모든 가능한 토큰 키 확인
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    
    if (!accessToken) {
      return { isValid: false, token: null };
    }
    
    const isValid = isTokenValid(accessToken);
    return { isValid, token: accessToken };
  }, []); // 의존성 배열 비워서 마운트 시 한 번만 확인
  
  // 토큰이 없을 때만 시스템 관리자 존재 여부 확인
  const shouldCheckSystemAdmin = !sessionStatus.token || !sessionStatus.isValid;
  const { data: systemAdminStatus, isLoading: isCheckingSystemAdmin } = useSystemAdminRegisterAvailable(shouldCheckSystemAdmin);
  // 로컬 스토리지에서 직접 시스템 관리자 여부 확인 (새로고침 시에도 동작)
  const isSystemAdminUser = isSystemAdmin();
  // 1. 인증 세션 확인 - 토큰이 없거나 만료된 경우에만 로그인으로 이동
  if (!sessionStatus.token || !sessionStatus.isValid) {
    // 시스템 관리자 존재 여부 확인 중이면 대기
    if (isCheckingSystemAdmin) {
      return null; // 또는 로딩 스피너 표시
    }
    
    // 시스템 관리자가 없으면 시스템 로그인 페이지로 이동
    if (systemAdminStatus?.status === true) {
      return <Navigate to={ROUTES.SYSTEM_LOGIN} replace />;
    }
    
    // 시스템 관리자가 있으면 일반 로그인 페이지로 이동
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  
  // 2. 페이지 접근 권한 확인 (로컬 스토리지에서 피처 권한 자동 로드) 시스템관리자는 무시하고 접근 가능
  if (!isSystemAdminUser && !hasPageAccess(path)) {
    // 접근 권한이 없는 경우 403 페이지로 리다이렉트
    return <Navigate to={ROUTES.FORBIDDEN} replace />;
  }
  
  // 3. 인증되었고 권한이 있는 경우 정상 접근
  return children;
};

export default ProtectedRoute; 